import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckCircle2, Briefcase, Gift, Star, ShieldCheck, Clock } from 'lucide-react';
import { US_STATES, SEMESTERS, DEGREE_LEVELS, CONCENTRATIONS, FORMATS } from './constants';

const PostFreeInternship = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [elig, setElig] = useState(null);
  const [plans, setPlans] = useState({});
  const [saving, setSaving] = useState(false);
  const [posted, setPosted] = useState(false);
  const [form, setForm] = useState({
    title: '', org_name: '', degree_level: 'Both', concentration: 'Generalist', location: '',
    state: 'Texas', format: 'in_person', semester: 'Fall', weekly_hours: '', population_served: '',
    start_date: '', application_deadline: '', supervision_included: true, description: '', contact_email: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadEligibility = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/internships/free-trial/eligibility`);
      setElig(r.data);
    } catch (e) { setElig({ authenticated: false, eligible: false }); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    document.title = 'Post a Free Internship (30 Days) | I Need A Social Worker';
    loadEligibility();
    axios.get(`${API_URL}/marketplace/internship-plans`).then((r) => setPlans(r.data.plans)).catch(() => {});
  }, [loadEligibility, isAuthenticated]);

  const startTrial = () => {
    if (!elig?.authenticated) {
      navigate('/register?type=employer&redirect=/post-free-internship');
      return;
    }
  };

  const submitTrial = async () => {
    if (!form.title || !form.org_name || !form.location || !form.description) {
      toast.error('Please complete title, organization, location and description.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/internships/free-trial`, form);
      setPosted(true);
      toast.success('Your free 30-day internship listing is live!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setSaving(false); }
  };

  const reactivate = async (planId) => {
    const id = elig?.trial_listing?.internship_id;
    if (!id) return;
    try {
      const res = await axios.post(`${API_URL}/marketplace/internship/checkout/stripe`, {
        plan_id: planId, origin_url: window.location.origin, internship_id: id,
      });
      window.location.href = res.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start checkout.'); }
  };

  const OfferHeader = () => (
    <div className="text-center mb-8">
      <Badge className="bg-secondary/15 text-secondary mb-3 inline-flex items-center gap-1" data-testid="free-trial-badge"><Gift className="w-3.5 h-3.5" /> Free 30-Day Trial</Badge>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-primary">Post Free Internship</h1>
      <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
        Reach motivated BSW and MSW students through INASW. Publish one internship opportunity free for 30 days.
        No credit card required and no automatic charges.
      </p>
      <div className="flex flex-wrap justify-center gap-4 mt-5 text-sm text-gray-600">
        <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-green-600" /> No credit card</span>
        <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-green-600" /> 30 days from publish</span>
        <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-600" /> One free listing per organization</span>
      </div>
    </div>
  );

  if (loading) return <div className="min-h-screen bg-gray-50 pt-32 text-center text-gray-500">Loading…</div>;

  const trialExpired = elig?.trial_listing && elig.trial_listing.status !== 'active';

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <OfferHeader />

        {/* Success after posting */}
        {posted ? (
          <div className="bg-white rounded-2xl border p-8 text-center" data-testid="free-trial-success">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h2 className="font-serif text-2xl font-bold text-primary">Your free listing is live!</h2>
            <p className="text-gray-600 mt-2">It will reach BSW and MSW students for the next 30 days. We’ll let you know before it expires.</p>
            <Button className="mt-5 bg-primary text-white" onClick={() => navigate('/find-an-internship')} data-testid="view-internships-btn">View internship directory</Button>
          </div>
        ) : !elig?.authenticated ? (
          /* Logged out — show CTA to sign in / create account */
          <div className="bg-white rounded-2xl border p-8 text-center" data-testid="free-trial-cta">
            <p className="text-gray-600 mb-5">Sign in or create a free employer account to publish your listing. Your 30 days begin the moment your internship is published.</p>
            <Button className="bg-secondary hover:bg-secondary-hover text-white h-11 px-8" onClick={startTrial} data-testid="start-free-trial-btn">
              Start My Free 30-Day Listing
            </Button>
            <p className="text-xs text-gray-400 mt-3">Already have an account? <button className="underline" onClick={() => navigate('/login?redirect=/post-free-internship')} data-testid="free-trial-signin">Sign in</button></p>
          </div>
        ) : trialExpired ? (
          /* Trial used and expired — reactivation prompt */
          <div className="bg-white rounded-2xl border p-8" data-testid="free-trial-expired">
            <h2 className="font-serif text-2xl font-bold text-primary text-center">Your free internship listing has expired.</h2>
            <p className="text-gray-600 mt-2 text-center max-w-xl mx-auto">Choose a paid posting option to reactivate your listing and continue reaching BSW and MSW students.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-6 max-w-2xl mx-auto">
              {['single', 'featured'].map((id) => {
                const p = plans[id]; if (!p) return null;
                return (
                  <div key={id} className="rounded-2xl border-2 border-gray-200 p-6 bg-white flex flex-col">
                    {id === 'featured' && <Badge className="bg-secondary text-white mb-2 w-fit flex items-center gap-1"><Star className="w-3 h-3" /> Featured</Badge>}
                    <h3 className="font-serif text-lg font-bold text-primary">{p.name}</h3>
                    <p className="text-3xl font-bold text-primary mt-1">${p.price}</p>
                    <ul className="mt-3 space-y-1.5 text-sm text-gray-600 flex-1">
                      {p.features.map((f) => <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> {f}</li>)}
                    </ul>
                    <Button className="mt-4 bg-secondary hover:bg-secondary-hover text-white" onClick={() => reactivate(id)} data-testid={`reactivate-${id}`}>Reactivate for ${p.price}</Button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : elig?.already_claimed ? (
          /* Trial active or already used (not expired) */
          <div className="bg-white rounded-2xl border p-8 text-center" data-testid="free-trial-active">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h2 className="font-serif text-2xl font-bold text-primary">You’ve used your free 30-day trial</h2>
            <p className="text-gray-600 mt-2">Your listing{elig.trial_listing?.title ? ` “${elig.trial_listing.title}”` : ''} is currently live. To post additional internships, choose a paid option.</p>
            <Button className="mt-5 bg-primary text-white" onClick={() => navigate('/post-an-internship')} data-testid="see-paid-options-btn">See posting options</Button>
          </div>
        ) : (
          /* Eligible — show the internship form */
          <div className="bg-white rounded-2xl border p-6 sm:p-8" data-testid="free-trial-form">
            <h2 className="font-serif text-xl font-bold text-primary mb-6 flex items-center gap-2"><Briefcase className="w-5 h-5 text-secondary" /> Internship Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Position title *"><Input value={form.title} onChange={(e) => set('title', e.target.value)} data-testid="ft-title" /></Field>
              <Field label="Organization *"><Input value={form.org_name} onChange={(e) => set('org_name', e.target.value)} data-testid="ft-org" /></Field>
              <Field label="Degree level"><SelectBox value={form.degree_level} onChange={(v) => set('degree_level', v)} options={DEGREE_LEVELS} testid="ft-degree" /></Field>
              <Field label="Concentration"><SelectBox value={form.concentration} onChange={(v) => set('concentration', v)} options={CONCENTRATIONS} testid="ft-concentration" /></Field>
              <Field label="Location (City, ST) *"><Input value={form.location} onChange={(e) => set('location', e.target.value)} data-testid="ft-location" /></Field>
              <Field label="State"><SelectBox value={form.state} onChange={(v) => set('state', v)} options={US_STATES} testid="ft-state" /></Field>
              <Field label="Format"><SelectBox value={form.format} onChange={(v) => set('format', v)} options={FORMATS} testid="ft-format" /></Field>
              <Field label="Semester"><SelectBox value={form.semester} onChange={(v) => set('semester', v)} options={SEMESTERS} testid="ft-semester" /></Field>
              <Field label="Weekly hours"><Input placeholder="e.g. 16-20 hrs/week" value={form.weekly_hours} onChange={(e) => set('weekly_hours', e.target.value)} data-testid="ft-hours" /></Field>
              <Field label="Population served"><Input value={form.population_served} onChange={(e) => set('population_served', e.target.value)} data-testid="ft-population" /></Field>
              <Field label="Start date"><Input placeholder="e.g. Aug 26, 2026" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="ft-start" /></Field>
              <Field label="Application deadline"><Input value={form.application_deadline} onChange={(e) => set('application_deadline', e.target.value)} data-testid="ft-deadline" /></Field>
            </div>
            <Field label="Description * (mention if paid or unpaid here)" className="mt-4">
              <Textarea rows={5} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Describe the placement, responsibilities, whether it is paid or unpaid, and requirements…" data-testid="ft-description" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-4">
              <Checkbox checked={form.supervision_included} onCheckedChange={(v) => set('supervision_included', !!v)} data-testid="ft-supervision" />
              Supervision included
            </label>
            <Button className="w-full mt-6 bg-secondary hover:bg-secondary-hover text-white h-11" onClick={submitTrial} disabled={saving} data-testid="submit-free-trial-btn">
              {saving ? 'Publishing…' : 'Start My Free 30-Day Listing'}
            </Button>
            <p className="text-xs text-gray-400 mt-3 text-center">No credit card required. No automatic charges. Your 30 days begin now, when the listing is published.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>
    {children}
  </div>
);

const SelectBox = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const lab = typeof o === 'string' ? o : o.label;
        return <SelectItem key={val} value={val}>{lab}</SelectItem>;
      })}
    </SelectContent>
  </Select>
);

export default PostFreeInternship;
